/**
 * Drawing a customer as their own logo.
 *
 * Why (2026-09-02)
 * ----------------
 * The world map drew 161 customers as 161 identical red teardrops. A map like
 * that can only answer "how many" — you cannot look at it and see that the
 * cluster in Pennsylvania is a state prison system, or find your largest
 * account, or notice that the pin you are hunting for is the one in Vietnam.
 * A logo answers "who" at a glance, which is the reason anybody opens a map of
 * their own customers.
 *
 * The design is a coloured RING around a white disc holding the logo. The ring
 * keeps everything the teardrop encoded — customer vs prospect, or the
 * product/feature palette — so nothing is lost by adding the logo; the white
 * disc is what makes a dark wordmark and a light one both legible on satellite
 * green and ocean blue alike.
 *
 * Two things this module is careful about:
 *
 *   1. It builds an HTML STRING for a Leaflet `divIcon`, which means React's
 *      escaping is not helping. `logoSrc` is validated against the exact shape
 *      crm-api emits and anything else is dropped — see `isSafeLogoSrc`.
 *   2. A logo that 404s must not leave a hole. The `<img>` removes itself on
 *      error, revealing the coloured disc underneath, so a broken image
 *      degrades to exactly the marker we drew before this existed.
 */

/** Marker diameter in CSS pixels. Big enough to recognise a logo, small enough
 * that a cluster of ten still reads as ten (`pin-spread` fans them 14px). */
export const LOGO_MARKER_SIZE = 28

/**
 * Words that are not the company. Taking initials from "Department of Veterans
 * Affairs" must give DV, not DO.
 */
const INITIAL_STOPWORDS = new Set(["of", "the", "and", "for", "a", "an", "at", "de", "la"])

/**
 * One or two letters standing in for a company that has no logo.
 *
 * Most customers have no logo and most never will: a state prison, an air base
 * and a county airport do not publish a brand mark, and 146 of 184 production
 * customers had none after the first sweep. Drawing those as an empty white
 * disc made the map look broken — a marker that is blank reads as a marker
 * that failed, not as a company without a logo.
 *
 * Initials are not a placeholder for a logo; they are the same information a
 * logo carries (which company is this?) in the form that always exists.
 */
export function companyInitials(name: string | null | undefined): string {
  const words = String(name ?? "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((word) => word.length > 0 && !INITIAL_STOPWORDS.has(word.toLowerCase()))

  if (words.length === 0) return "?"
  // A single word gives one letter, not two: "Blackberry" as "BL" reads like
  // an abbreviation of something else.
  if (words.length === 1) return words[0]!.slice(0, 1).toUpperCase()
  return (words[0]!.slice(0, 1) + words[1]!.slice(0, 1)).toUpperCase()
}

/**
 * The one shape a logo `src` is allowed to have: the relative crm-api image
 * path, optionally prefixed by the dashboard origin that `crmApiUrl()` adds
 * for the desktop shell.
 *
 * A Leaflet `divIcon` takes raw HTML, so this value is interpolated into an
 * attribute without any framework escaping. The payload arrives from our own
 * API — but "it came from our API" is exactly the assumption that turns one
 * stored-XSS bug into a rendered one, and the value is cheap to constrain: it
 * is always `/api/v1/image/<active-storage-key>.png`.
 */
export function isSafeLogoSrc(src: string | null | undefined): src is string {
  if (typeof src !== "string" || src.length === 0 || src.length > 512) return false
  return /^(https?:\/\/[A-Za-z0-9.-]+(?::\d+)?)?\/api\/v1\/image\/[A-Za-z0-9_-]{8,128}\.(?:png|jpe?g|gif|webp)$/.test(
    src,
  )
}

/**
 * The marker's inner HTML.
 *
 * Split out from the Leaflet call so it can be asserted on without a DOM, a
 * map, or a network — the escaping rule above is the kind of thing that needs
 * a test naming it.
 */
export function logoMarkerHtml(options: {
  logoSrc?: string | null
  color: string
  label?: string | null
}): string {
  const { color, label } = options
  const size = LOGO_MARKER_SIZE
  const inner = size - 6
  const src = isSafeLogoSrc(options.logoSrc) ? options.logoSrc : null

  // `title` is a plain attribute in a string, so it gets the same treatment as
  // the src: escaped, not trusted. Company names contain apostrophes and
  // ampersands as a matter of course.
  const title = escapeAttribute(label ?? "")

  // The company name is a `title` on the WRAPPER only. Putting it on the image
  // too gave every marker with a logo two elements carrying the same tooltip,
  // which is one hover target too many and makes `[title="…"]` ambiguous.
  // The initials sit UNDER the image, not instead of it, so a logo that 404s
  // (blob pruned, storage volume replaced) reveals them when it removes itself
  // rather than leaving a blank disc.
  const initials =
    `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;` +
    `font:600 ${src ? 9 : 10}px/1 Inter,system-ui,sans-serif;color:${color};letter-spacing:0.02em;` +
    `user-select:none">${escapeAttribute(companyInitials(label))}</span>`

  const image = src
    ? `<img src="${src}" alt="" ` +
      `style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;` +
      `border-radius:50%;display:block;background:#fff" ` +
      `onerror="this.remove()">`
    : ""

  return (
    `<div title="${title}" style="` +
    `width:${size}px;height:${size}px;border-radius:50%;` +
    `background:${color};` +
    `box-shadow:0 1px 4px rgba(0,0,0,0.45);` +
    `display:flex;align-items:center;justify-content:center;">` +
    `<div style="position:relative;width:${inner}px;height:${inner}px;border-radius:50%;` +
    `background:#fff;overflow:hidden;">` +
    initials +
    image +
    `</div></div>`
  )
}

/** The five characters that can break out of a double-quoted HTML attribute. */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}
